import React, { createContext, useContext, useState, useEffect } from 'react';
import { addDocSafe } from '../firebase';

const ConsentContext = createContext();

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState({
    necessary: true, // Always true
    analytics: false,
    marketing: false,
    preferences: false
  });
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem('gdpr-consent');
    if (savedConsent) {
      try {
        const parsedConsent = JSON.parse(savedConsent);
        setConsent(parsedConsent);
        updateGoogleConsent(parsedConsent);
      } catch (error) {
        console.error('Error parsing saved consent:', error);
      }
    } else {
      // Show banner if no consent has been given
      setIsBannerVisible(true);
    }
  }, []);

  // Update Google Consent Mode v2
  const updateGoogleConsent = (consentData) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: consentData.analytics ? 'granted' : 'denied',
        ad_storage: consentData.marketing ? 'granted' : 'denied',
        ad_user_data: consentData.marketing ? 'granted' : 'denied',
        ad_personalization: consentData.marketing ? 'granted' : 'denied',
        personalization_storage: consentData.preferences ? 'granted' : 'denied',
        functionality_storage: 'granted', // Always granted for basic functionality
        security_storage: 'granted' // Always granted for security
      });
    }
  };

  // Save consent to localStorage and Firestore
  const saveConsent = async (newConsent) => {
    const consentData = {
      ...newConsent,
      necessary: true, // Always true
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      ipAddress: null // Will be populated by Cloud Function if needed
    };

    // Save to localStorage
    localStorage.setItem('gdpr-consent', JSON.stringify(consentData));
    setConsent(consentData);

    // Update Google Consent Mode
    updateGoogleConsent(consentData);

    // Save to Firestore
    try {
      await addDocSafe('consents', consentData);
    } catch (error) {
      console.error('Error saving consent to Firestore:', error);
    }

    // Hide banner and settings
    setIsBannerVisible(false);
    setIsSettingsOpen(false);
  };

  // Accept all cookies
  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    });
  };

  // Accept only necessary cookies
  const acceptNecessary = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    });
  };

  // Save custom preferences
  const saveCustomConsent = (customConsent) => {
    saveConsent({
      necessary: true,
      ...customConsent
    });
  };

  // Open settings modal
  const openSettings = () => {
    setIsSettingsOpen(true);
  };

  // Close settings modal
  const closeSettings = () => {
    setIsSettingsOpen(false);
  };

  // Check if consent has been given
  const hasConsent = () => {
    return localStorage.getItem('gdpr-consent') !== null;
  };

  // Get consent for specific purpose
  const hasConsentFor = (purpose) => {
    return consent[purpose] === true;
  };

  const value = {
    consent,
    isBannerVisible,
    isSettingsOpen,
    acceptAll,
    acceptNecessary,
    saveCustomConsent,
    openSettings,
    closeSettings,
    hasConsent,
    hasConsentFor,
    setIsBannerVisible
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};
